import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import React from "react";
import TetherComponent from "react-tether";
import { TextField } from "@material-ui/core";

import { useDispatch } from "react-redux";

import {
  CheckmarkIcon,
  DragReorderIcon,
} from "./components/icons/themes/windows10";
import useOnClickOutside from "./hooks/on-click-outside";
import useCurrentEnvironment from "./hooks/use-environment";
import useAccountMetadata from "./hooks/useAccountMetadata";
import {
  fetchAccountMetadata,
  setCurrentEnvironment,
} from "./actions/environments";
import { useAlert } from "react-alert";
import { useQueryClient } from "react-query";

const useStyles = makeStyles(() => ({
  root: {
    boxShadow: "none",
    backgroundColor: "#232F3E",
  },
  tetherTriangle: {
    "&::after": {
      position: "absolute",
      content: "' '",
      fontSize: 0,
      lineHeight: 0,
      width: 0,
      top: 9,
      right: -10,
      borderTop: "10px solid transparent",
      borderBottom: "10px solid transparent",
      borderLeft: "10px solid white",
    },
  },
  panel: {
    width: 300,
    paddingTop: 4,
    maxHeight: "95vh",
    overflowY: "auto",
  },
  environmentDisplay: {
    width: 90,
  },
}));

export const NavigationEnvironmentPicker = () => {
  const [open, setOpen] = React.useState(false);
  const accountMetadata = useAccountMetadata();
  const { projects } = accountMetadata;
  const dispatch = useDispatch();
  const displayRef = React.useRef();
  const panelRef = React.useRef();
  const alert = useAlert();
  const queryClient = useQueryClient();

  useOnClickOutside([displayRef, panelRef], () => {
    setOpen(false);
  });

  const setEnvironmentAndProjectFromPanel = React.useCallback(
    (environment) => {
      dispatch(setCurrentEnvironment(environment.ID, queryClient));
      setOpen(false);
    },
    [dispatch]
  );

  const toggleOpen = React.useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  React.useEffect(() => {
    dispatch(fetchAccountMetadata(alert));
    // eslint-disable-next-line
  }, []);

  return (
    <TetherComponent
      attachment="top right"
      style={{ zIndex: 99999999999 }}
      offset="39px 20px"
      renderTarget={(ref) => {
        return (
          <div ref={displayRef}>
            <EnvironmentDisplay ref={ref} onClick={toggleOpen} />
          </div>
        );
      }}
      renderElement={(ref) => {
        return (
          <>
            {open && (
              <div ref={panelRef}>
                <EnvironmentPickerPanel
                  setEnvironmentAndProject={setEnvironmentAndProjectFromPanel}
                  projects={projects}
                  ref={ref}
                />
              </div>
            )}
          </>
        );
      }}
    />
  );
};

const EnvironmentDisplay = React.forwardRef(({ onClick }, ref) => {
  const classes = useStyles();

  const currentEnvironment = useCurrentEnvironment();

  return (
    <div
      className={clsx("flex items-center cursor-pointer group justify-end")}
      onClick={onClick}
      ref={ref}
    >
      <DragReorderIcon className="fill-gray-300 opacity-50 mr-4 group-hover:opacity-100" />
      <div
        className={clsx("text-right text truncate", classes.environmentDisplay)}
      >
        <div className="text-gray-500 font-bold text-2xs truncate">
          {currentEnvironment &&
            currentEnvironment.project &&
            currentEnvironment.project.name}
        </div>
        <div className="text-gray-100 text-sm truncate">
          {currentEnvironment && currentEnvironment.name}
        </div>
      </div>
    </div>
  );
});

const EnvironmentPickerPanel = React.forwardRef(
  ({ projects, setEnvironmentAndProject }, ref) => {
    const classes = useStyles();
    const currentEnvironment = useCurrentEnvironment();
    const [searchString, setSearchString] = React.useState("");
    const [projectsToShow, setProjectsToShow] = React.useState(projects);

    const envRowClasses = (environment) =>
      clsx(
        "flex justify-between items-center px-4 py-2 text-gray-800 text-sm hover:bg-gray-200 cursor-pointer truncate",
        {
          "bg-gray-200":
            currentEnvironment && currentEnvironment.ID === environment.ID,
        }
      );

    React.useEffect(() => {
      // clone projects for mutation
      // Note: Doesn't seem to be particularly bad for memory since it seems
      // to get cleaned up after the useEffect
      const projs = JSON.parse(JSON.stringify(projects));
      setProjectsToShow(
        projs
          .map((proj) => {
            const envs = proj.environments.filter((env) => {
              // perform filtering at env level checking both project and
              // env name
              return (
                proj.name.toLowerCase().includes(searchString) ||
                env.name.toLowerCase().includes(searchString)
              );
            });
            proj.environments = _.sortBy(envs, "createdAt");
            return proj;
          })
          // filter out any projects without env/proj matches
          .filter((proj) => proj.environments.length)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    }, [searchString]);

    return (
      <div
        ref={ref}
        className={clsx(
          "bg-white pb-4 rounded shadow-md",
          classes.tetherTriangle,
          classes.panel
        )}
      >
        <TextField
          className="w-full"
          name="search"
          value={searchString}
          variant="outlined"
          onChange={(e) => setSearchString(e.target.value.toLowerCase())}
          InputProps={{
            className: clsx(classes.searchField),
            placeholder: "Search environments/projects",
          }}
        />
        {projectsToShow.length === 0 ? (
          <div className="px-4 mt-4 text-gray-500 font-bold text-xs truncate">
            No projects or environments found
          </div>
        ) : (
          projectsToShow.map((project, index) => (
            <div key={project.ID} className="mt-4 mb-1">
              <div className="px-4 mb-2 text-gray-500 font-bold text-xs truncate">
                {project.name}
              </div>
              {project.environments.map((environment) => (
                <div
                  key={environment.ID}
                  onClick={() => setEnvironmentAndProject(environment, project)}
                  className={envRowClasses(environment)}
                >
                  {environment.name}
                  {currentEnvironment &&
                    currentEnvironment.ID === environment.ID && (
                      <CheckmarkIcon className="fill-green-700 mr-2" />
                    )}
                </div>
              ))}
              {index !== projectsToShow.length - 1 && (
                <div className="border-b w-full" />
              )}
            </div>
          ))
        )}
      </div>
    );
  }
);
